import type { NumberFormatOptions } from '@formatjs/ecma402-abstract';
import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { SolvedFieldsRatioLabel } from 'components/common-form/builder/form/node/field/SolvedFieldsRatioLabel';
import { FxSpot } from 'components/common-form/sidePanel/status/fxSpotUtils';
import { PriceSidePanelStatus } from 'components/shared/Price/shared';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, FormattedNumber, FormattedRelativeTime, useIntl } from 'react-intl';
import type { GreekLine } from 'state/quotes/details/details.model';
import type { SolveResultFormat } from 'state/quotes/results/result.model';
import styled from 'styled-components';
import { xlDateTimeFormatOptions } from 'utils/core/date';
import { dateNow } from 'utils/core/now';

export interface PriceProps {
  price?: number;
  priceFormat?: SolveResultFormat;
  fxSpot?: GreekLine | null;
  topLeft?: ReactNode;
  bottomLeft?: ReactNode;
  bottomRight?: ReactNode;
  color: 'success' | 'info' | 'warning' | 'danger' | 'primary' | 'secondary';
  strike?: boolean;
  actions?: ReactNode;
}

const RateZone = styled.h1`
  font-size: 42px;
`;

function mapPriceFormatToIntl(priceFormat?: SolveResultFormat): NumberFormatOptions['style'] {
  return priceFormat === 'Percent' || priceFormat === undefined ? 'percent' : undefined;
}

export function Price({
  color,
  bottomLeft,
  topLeft,
  bottomRight,
  price,
  priceFormat,
  fxSpot = null,
  strike = false,
  actions,
}: PriceProps): React.JSX.Element {
  const { formatMessage } = useIntl();
  return (
    <PriceSidePanelStatus color={color} topLeft={topLeft} topRightActions={actions}>
      <div className="flex flex-grow-1 flex-column align-items-end p-3">
        {price === undefined ? null : (
          <RateZone
            className={classNames('fw-medium tabular-figures', { 'text-decoration-line-through': strike })}
            data-e2e="solve-result"
            title={formatMessage({ id: 'solveValue' })}
          >
            <FormattedNumber value={price} maximumFractionDigits={4} style={mapPriceFormatToIntl(priceFormat)} />
          </RateZone>
        )}
        {<FxSpot greekLine={fxSpot} isObsolete={strike} />}
        <SolvedFieldsRatioLabel className="fw-extrabold align-middle" e2e="solving-fields-ratio" />
        <div
          className="align-middle text-large text-nowrap"
          data-e2e="flow-sense"
          title={formatMessage({ id: 'flowSense.title' })}
        >
          {bottomLeft}
        </div>
      </div>
      <div className="flex flex-shrink-1 align-items-end p-3">
        <div className="fs-6" data-e2e="priced-on">
          {bottomRight}
        </div>
      </div>
    </PriceSidePanelStatus>
  );
}

interface PricedOnProps {
  className?: string;
  pricedOn: string;
  getNow?(): number;
}

const DisplayWhen = styled.span<{ $over?: boolean; width: number }>`
  display: ${({ $over }) => ($over ? 'none' : 'inline')};
  @media screen and (min-width: ${({ width }) => width}px) {
    display: ${({ $over }) => ($over ? 'inline' : 'none')};
  }
`;

const compactDateBreakPoint = 1775;

export function PricedOn({ className, pricedOn, getNow = dateNow }: PricedOnProps) {
  const { formatDate } = useIntl();
  const relativeTime = useMemo(
    () => (pricedOn ? (new Date(pricedOn).getTime() - getNow()) / 1_000 : 0),
    [pricedOn, getNow],
  );
  if (!isDefined(pricedOn)) {
    return null;
  }
  const formattedPricedOn = formatDate(pricedOn, xlDateTimeFormatOptions);
  return (
    <>
      <DisplayWhen $over width={compactDateBreakPoint} className={classNames(className, 'text-nowrap')}>
        <FormattedMessage id="sidePanel.pricedOn" defaultMessage="Priced on " />
        {formattedPricedOn}
      </DisplayWhen>
      <DisplayWhen width={compactDateBreakPoint}>
        <span title={formattedPricedOn}>
          <FormattedRelativeTime value={relativeTime} updateIntervalInSeconds={1} style="short" />
        </span>
      </DisplayWhen>
    </>
  );
}
